import { createApi } from "@reduxjs/toolkit/query/react";
import { get } from "axios";
import { AuthHeaders, baseQueryWithAuthInterceptor, decryptResponse } from "./util";
var qs = require('qs');

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery: baseQueryWithAuthInterceptor({ baseUrl: "/api/orders" }),
  tagTypes: ["Order"],
  endpoints: (builder) => ({
    getOrders: builder.query({
      query: (body) => {
        const opts = {};
        if (body && body.driverId) {
          opts.params = { driverId: body.driverId };
        }
        if (body && body.status) {
          opts.params = { status: body.status };
        }
        if (body && body.day) {
          opts.params = { day: body.day };
        }
        if (body && body.fromDate && body.toDate) {
          opts.params = { fromDate: body.fromDate, toDate: body.toDate };
        }
        if (body && body.ids) {
          opts.params = { ids: JSON.stringify(body.ids) };
        }
        return {
          url: "",
          ...opts,
        };
      },
      providesTags: ["Order"],
    }),
    getOrder: builder.query({
      query: (id) => id,
      providesTags: ["Order"],
    }),
    updateOrder: builder.mutation({
      query: (body) => ({
        url: body._id,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    createOrder: builder.mutation({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => !error && ["Order"],
    }),
    changeStatus: builder.mutation({
      query: (body) => ({
        url: `${body._id}/${body.status}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    removeOrder: builder.mutation({
      query: (body) => ({
        url: "cancel",
        method: "DELETE",
        body,
      }),
      invalidatesTags: (result, error, arg) => !error && ["Order"],
    }),
    importOrders: builder.mutation({
      query: (body) => ({
        url: "import",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => !error && ["Order"],
    }),
    getApprovedOrders: builder.query({
      query: (body) => {
        const opts = {};

        opts.params = { status: "approved" };
        return {
          url: "",
          ...opts,
        };
      },
      providesTags: ["Order"],
    }),
    rejectOrderPayment: builder.mutation({
      query: (body) => ({
        url: `${body._id}/rejectPayment`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    approveOrderPayment: builder.mutation({
      query: (body) => ({
        url: `${body._id}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    getEstimatedPayment: builder.query({
      query: () => "get/estimatedPay",
      providesTags: ["Admin"],
    }),
    swapShift: builder.mutation({
      query: (body) => ({
        url: `${body._id}/swap`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    InterestedDriver: builder.query({
      query: (body) => {
        return {
          url: `/intrested-count/drivers?fromDate=${body?.id?.fromDate}&toDate=${body?.id?.toDate}&metro=${body?.id?.metro}&status=${body?.id?.status}&hub=${body?.id?.hub}&shiftType=${body?.id?.shiftType}`,
          method: "GET",
        };
      },
    }),
    getPodData: builder.query({
      query: (id) => {
        return {
          url: `/pod/${id}`,
          method: "GET",
        };
      },
    }),
    getUnaccountedPackages: builder.query({
      query: (id) => {
        return {
          url: `/${id}/unaccounted-packages`,
          method: "GET",
        };
      },
    }),
    updateBulkOrder: builder.mutation({
      query: (body) => ({
        url: "/bulkUpdate",
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    updateBulkDriverAssign: builder.mutation({
      query: (body) => ({
        url: "/bulkAssign",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        !error && [{ type: "Order", _id: result._id }],
    }),
    getZonesByMetro: builder.mutation({
      query: (body) => ({
        url: "/get-zones-by-metro",
        method: "POST",
        body
      }),
      transformResponse: (response) => {
        if (response?.error === true) {
          throw new Error(response?.message || "Something went wrong");
        }
        return response?.data?.zones || []
      },
      transformErrorResponse: (response) => {
        return response?.data?.message || "Something went wrong";
      },
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  useUpdateOrderMutation,
  useCreateOrderMutation,
  useChangeStatusMutation,
  useRemoveOrderMutation,
  useImportOrdersMutation,
  useGetApprovedOrdersQuery,
  useRejectOrderPaymentMutation,
  useApproveOrderPaymentMutation,
  useGetEstimatedPaymentQuery,
  useSwapShiftMutation,
  useInterestedDriverQuery,
  useGetPodDataQuery,
  useLazyGetPodDataQuery,
  useGetUnaccountedPackagesQuery,
  useUpdateBulkOrderMutation,
  useUpdateBulkDriverAssignMutation,
  useGetZonesByMetroMutation
} = ordersApi;

const ERROR_MSG = "Something went wrong";

const handleQuery = (query) => {
  let queryStr = "";
  Object.keys(query).forEach((key, idx) => {
    if (key === "status" && query[key] === "all") return;
    const isFirstQuery = idx < 1 ? "?" : "&";
    const isValue = query[key] ? `${isFirstQuery}${key}=${query[key]}` : "";
    return (queryStr += isValue);
  });
  return queryStr;
};

export const getOrdersList = async (query) => {
  const { fromDate, toDate, isTotal, page, limit, search, metro, status, reasons, shiftType, hub, isShiftRejectReason, isArriveHub, routeCode, isExport, shiftMode, vehicleType } = query
  const queryStr = `/api/orders?fromDate=${fromDate}&toDate=${toDate}&page=${page || ""}&limit=${limit || ""}&isTotal=${isTotal}&search=${search}&isRejectReason=${isShiftRejectReason}&isDriverArrived=${isArriveHub}&routecode=${routeCode || ""}&shiftMode=${shiftMode || ""}&isExport=${isExport || ""}`
  const getShifts = await get(queryStr, {
    'params': { 'metro': metro, "status": status, "shiftType": shiftType, "hub": hub, "reasons": reasons, "vehicleType": vehicleType },
    'paramsSerializer': function (params) {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    },
    'headers': AuthHeaders()
  });
  try {
    if (getShifts.status === 200) getShifts.data = decryptResponse(getShifts?.data);
    const { status, data } = getShifts || {};
    if (status === 200 && data) return { shiftsData: data };
    else return ERROR_MSG;
  } catch (error) {
    return error?.response?.data || ERROR_MSG;
  }
};

export const getOrdersCount = async (query) => {
  const { fromDate, toDate, search, metro, status, reasons, shiftType, hub, isShiftRejectReason, isArriveHub, routeCode, shiftMode, vehicleType } = query
  const queryStr = `/api/shifts/count?fromDate=${fromDate}&toDate=${toDate}&search=${search}&isRejectReason=${isShiftRejectReason}&isDriverArrived=${isArriveHub}&shiftMode=${shiftMode || ""}&routecode=${routeCode || ""}`
  const shiftsCountData = await get(queryStr, {
    'params': { 'metro': metro, "status": status, "shiftType": shiftType, "hub": hub, "reasons": reasons, "vehicleType": vehicleType },
    'paramsSerializer': function (params) {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    },
    'headers': AuthHeaders()
  });
  try {
    if (shiftsCountData.status === 200) shiftsCountData.data = decryptResponse(shiftsCountData?.data);
    const { status, data } = shiftsCountData || {};
    if (status === 200 && data?.result) return { shiftCountData: data?.result };
    else return ERROR_MSG;
  } catch (error) {
    return error?.response?.data || ERROR_MSG;
  }
};
